import CardCommon from '@op/components/CardCommon';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import React, { useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import { tableColumnsList, tableColumnsMap } from '../constants';
import { useTrendTimeline } from '../hooks';
import { formatTimeXAxisText } from '@op/utils/chart';
import { DBO_V, DBO_Y } from '@op/utils/format';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { formatDateFunc } from '@op/utils/time';
import { getChartBufferWidth } from '@op/utils/systemUtil';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { useDownloadParams } from '@op/hooks/useDownloadParams';

export default function Trend({
  filter,
  gameInfoMap,
  selectedKeysMap,
  loading,
}: {
  filter: any;
  gameInfoMap: any;
  selectedKeysMap?: any;
  loading?: boolean;
}) {
  const [tab, setTab] = useState('views');
  const { download, downloading } = useDownloadParams();

  const { data, loading: chartLoading, tableRecode } = useTrendTimeline({
    filter,
    gameInfoMap,
    tab,
    selectedKeysMap,
  });

  const { formatOptionsMessage, formatMessage } = useLanguage();

  const { options, isEmpty } = useMemo(() => {
    const isPercentage = tab === 'market_share_by_view';

    const valueFormat = isPercentage ? DBO_V.ratio_min : DBO_V.reviews;

    const { series, xAxis, legend, isEmpty } = data || {};

    if (isEmpty) {
      return { options: {}, isEmpty };
    }

    return {
      isEmpty,
      options: {
        grid: {
          top: 20,
          // left: getChartBufferWidth() + 15,
        },
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) => {
            let { name } = data?.[0] || {};
            const time = listTimeTitleFunc(name, filter.date_type);
            return formatChartParamsDate({
              params: data,
              valueFormat: (value) => valueFormat(value),
              tablesConfigFunc: (records: Array<any>) => {
                let columns = [
                  {
                    key: 'text',
                    header: time,
                  },
                  {
                    key: 'value',
                    weight: 'bold',
                    align: 'right',
                    header: formatMessage({ id: tableColumnsMap[tab] }),
                  },
                ];
                return [{ showHeader: true, records, columns }];
              },
              dealwithRecords: (records: Array<any>) =>
                records
                  .map((item, index) => {
                    const { time } = data[index].data;
                    let timeText = '';
                    if (filter?.date_type) {
                      timeText = formatDateFunc(filter?.date_type)(time);
                    }
                    return { ...item, date: timeText };
                  })
                  .sort((a, b) => b.originValue - a.originValue),
              textShowFun: (marker: string, seriesName: string, data) => {
                const { entity_name, cover } = gameInfoMap?.[seriesName] || {};
                return `<span class="chart-common-formatter-marker dbo-game-comparison-chart-tooltip">${marker}<div class="game-icon" style="background-image: url(${encodeURI(cover)});">
                     </div> ${entity_name}</span>`;
              },
            });
          },
          appendToBody: true,
        },
        legend: {
          data: legend,
          formatter: (value: string) => {
            const { entity_name } = gameInfoMap?.[value] || {};
            return `{name|${entity_name}}`;
          },
        },
        xAxis: {
          data: xAxis,
          axisLabel: {
            fontSize: 12,
            formatter: (value: any) => {
              return formatTimeXAxisText(value, filter?.date_type);
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return DBO_Y.reviewsPercent(value, isPercentage, true);
            },
          },
        },
        series: series,
      },
    };
  }, [data]);

  return (
    <CardCommon
      loading={loading}
      loadingHeight={344}
      empty={!filter}
      emptyHeight={344}
      title={formatMessage({ id: 'Trend' })}
      hasDownload
      downloadLoading={downloading}
      handleDownloadOnClick={() => {
        download({
          key: "MarketPopularityRankingTrend",
          data: tableRecode,
          filter,
          fileName: "Market Popularity Ranking_Trend"
        })
      }}
    >
      <Segment
        // className="segment-light segment-font12"
        value={tab}
        onChange={(value) => {
          setTab(value);
          buriedBtnPoint('SwitchTrendMetrics', { tab: value });
        }}
        options={formatOptionsMessage(tableColumnsList, undefined, { placement: 'top' })}
      />

      <EChartCommon
        options={options}
        empty={isEmpty}
        loading={chartLoading}
        height={300}
        optionsProps={{ type: 'line' }}
      ></EChartCommon>
    </CardCommon>
  );
}
